import Color from 'color';
import type { IRunPropertiesOptions, IRunStylePropertiesOptions } from 'docx';
import {
  DeletedTextRun,
  InsertedDeletedTextRun,
  InsertedTextRun,
  ShadingType,
  TextRun,
  UnderlineType,
} from 'docx';
import { isEmpty, pick, uniq } from 'ramda';
import type { NodeEntry, Path } from 'slate';

import { getEditDate, surroundTextWithComments } from './internal';
import type { DOCXCommentRange } from './internal';
import type { BaseDocxOptions } from './types';
import type { AlineText, AlineTextFormats } from '../../../slate-api';
import { isDeleteEdit, isInsertEdit, Queries } from '../../../slate-api';
import { ptToHalfPts } from '../../../util';
import { toHex } from '../../../util/color';

export type DOCXTextRun = TextRun | InsertedTextRun | DeletedTextRun | InsertedDeletedTextRun;
export type DOCXInline = DOCXTextRun | DOCXCommentRange;

export function createTextRuns(
  textEntries: NodeEntry<AlineText>[],
  options: BaseDocxOptions
): DOCXInline[] {
  return textEntries.flatMap(textEntry => createTextRun(textEntry, options));
}

/**
 * Create a DOCXTextRun from an AlineText node.
 */
export function createTextRun(
  // Use partial for type safety.
  textEntry: [Partial<AlineText>, Path],
  options: BaseDocxOptions
): DOCXInline[] {
  const { context } = options;
  const [textNode] = textEntry;
  const runProperties = createRunProperties(Queries.getActiveTextProps(textNode));
  const suggestedInsertEdit = textNode.suggestedEdits?.find(isInsertEdit);
  const suggestedDeleteEdit = textNode.suggestedEdits?.find(isDeleteEdit);
  const { authorIds, date, suggestedProps } = Queries.getSuggestedTextProps(textNode);
  const textPropsBeforeSuggestion = pick(Object.keys(suggestedProps), textNode);
  const runPropertiesBeforeSuggestion = createRunProperties(textPropsBeforeSuggestion);
  const applyRevision = !isEmpty(suggestedProps);
  const commonRunProperties: IRunPropertiesOptions = {
    ...runProperties,
    /**
     * Note: The DOCX library is weird in that, for `revision` property, the *PREVIOUS* text properties are used.
     * See https://docx.js.org/#/usage/change-tracking.
     */
    revision: applyRevision
      ? ({
          author: uniq(authorIds)
            .map(id => context.getAuthorName(id))
            .join(', '),
          date,
          id: context.generateEditId(),
          ...runPropertiesBeforeSuggestion,
        } satisfies IRunPropertiesOptions['revision'])
      : undefined,
  };

  if (suggestedInsertEdit && suggestedDeleteEdit) {
    const insertedDeletedTextRun = new InsertedDeletedTextRun({
      ...commonRunProperties,
      deletion: {
        author: context.getAuthorName(suggestedDeleteEdit.authorId),
        date: getEditDate(suggestedDeleteEdit),
        id: context.generateEditId(suggestedDeleteEdit.groupId),
      },
      insertion: {
        author: context.getAuthorName(suggestedInsertEdit.authorId),
        date: getEditDate(suggestedInsertEdit),
        id: context.generateEditId(suggestedInsertEdit.groupId),
      },
      text: textNode.text,
    });

    return surroundTextWithComments(textEntry, { context, textRun: insertedDeletedTextRun });
  }

  if (suggestedInsertEdit) {
    const insertedTextRun = new InsertedTextRun({
      ...commonRunProperties,
      author: context.getAuthorName(suggestedInsertEdit.authorId),
      date: getEditDate(suggestedInsertEdit),
      id: context.generateEditId(suggestedInsertEdit.groupId),
      text: textNode.text,
    });

    return surroundTextWithComments(textEntry, { context, textRun: insertedTextRun });
  }

  if (suggestedDeleteEdit) {
    const deletedTextRun = new DeletedTextRun({
      ...commonRunProperties,
      author: context.getAuthorName(suggestedDeleteEdit.authorId),
      date: getEditDate(suggestedDeleteEdit),
      id: context.generateEditId(suggestedDeleteEdit.groupId),
      text: textNode.text,
    });

    return surroundTextWithComments(textEntry, { context, textRun: deletedTextRun });
  }

  const textRun = new TextRun({
    ...commonRunProperties,
    text: textNode.text,
  });

  return surroundTextWithComments(textEntry, { context, textRun });
}

export function createRunProperties(textFormats: Partial<AlineTextFormats>): IRunPropertiesOptions {
  const bold = createBold(textFormats);
  const italics = createItalics(textFormats);
  const strikethrough = createStrikethorugh(textFormats);
  const underline = createUnderline(textFormats);
  const fontSize = createFontSize(textFormats);
  const fontType = createFontType(textFormats);
  const fontColor = createFontColor(textFormats);
  const shading = createShading(textFormats);
  const superscript = createSuperscript(textFormats);
  const subscript = createSubscript(textFormats);

  return {
    ...bold,
    ...italics,
    ...strikethrough,
    ...underline,
    ...fontType,
    ...fontSize,
    ...fontColor,
    ...shading,
    ...superscript,
    ...subscript,
  };
}

// Underline color is in hex per http://officeopenxml.com/WPtextFormatting.php.
function createUnderline(
  textFormats: Partial<AlineTextFormats>
): Pick<IRunStylePropertiesOptions, 'underline'> {
  if (typeof textFormats.underline?.active !== 'boolean') return {};
  if (!textFormats.underline.active) return { underline: { type: UnderlineType.NONE } };

  try {
    return {
      underline: {
        color: textFormats.fontColor?.color ? toHex(textFormats.fontColor.color) : undefined,
        type: UnderlineType.SINGLE,
      },
    };
  } catch (err) {
    console.error(err.message);
    return { underline: { type: UnderlineType.SINGLE } };
  }
}

/**
 * It seems we want to use "shading" vs. "highlight" in Word.  http://officeopenxml.com/WPtextShading.php.
 * Shading permits using hex colors whereas highlighting has a *very* specific color palette that doesn't translate well from hex/hsl/rgb.  This is also what Google does
 * when exporting to Word because Word highlighting doesn't support the full color palette used for highlighting in Google Docs.
 */
function createShading(
  textFormats: Partial<AlineTextFormats>
): Pick<IRunStylePropertiesOptions, 'shading'> {
  if (!textFormats.highlight?.color) return {};

  try {
    const highlightColor = Color(textFormats.highlight.color);
    return {
      shading: {
        color: highlightColor.hex(),
        fill: highlightColor.hex(),
        type: ShadingType.CLEAR,
      },
    };
  } catch (err) {
    console.error(err.message);
    return {};
  }
}

// Font color is in hex per http://officeopenxml.com/WPtextFormatting.php.
function createFontColor(
  textFormats: Partial<AlineTextFormats>
): Pick<IRunStylePropertiesOptions, 'color'> {
  if (!textFormats.fontColor?.color) return {};

  try {
    return {
      color: Color(textFormats.fontColor.color).hex(),
    };
  } catch (err) {
    console.error(err.message);
    return {};
  }
}

function createBold(
  textFormats: Partial<AlineTextFormats>
): Pick<IRunStylePropertiesOptions, 'bold'> {
  if (typeof textFormats.bold?.active !== 'boolean') return {};
  return { bold: textFormats.bold.active };
}

function createSuperscript(
  textFormats: Partial<AlineTextFormats>
): Pick<IRunStylePropertiesOptions, 'superScript'> {
  if (textFormats.verticalAlign?.align !== 'superscript') return {};
  return { superScript: true };
}

function createSubscript(
  textFormats: Partial<AlineTextFormats>
): Pick<IRunStylePropertiesOptions, 'subScript'> {
  if (textFormats.verticalAlign?.align !== 'subscript') return {};
  return { subScript: true };
}

function createFontType(
  textFormats: Partial<AlineTextFormats>
): Pick<IRunStylePropertiesOptions, 'font'> {
  if (typeof textFormats.fontType?.type !== 'string') return {};
  // It seems that our font "type" plays nicer with Word vs. a long CSS font family string, so we use fontType.type vs. fontType.fontFamily here.
  return { font: textFormats.fontType.type };
}

function createFontSize(
  textFormats: Partial<AlineTextFormats>
): Pick<IRunStylePropertiesOptions, 'size'> {
  if (typeof textFormats.fontSize?.size !== 'number') return {};
  // Specifies the font size in half points per http://officeopenxml.com/WPtextFormatting.php.
  return { size: ptToHalfPts(textFormats.fontSize.size) };
}

function createItalics(
  textFormats: Partial<AlineTextFormats>
): Pick<IRunStylePropertiesOptions, 'italics'> {
  if (typeof textFormats.italic?.active !== 'boolean') return {};
  return { italics: textFormats.italic.active };
}

function createStrikethorugh(
  textFormats: Partial<AlineTextFormats>
): Pick<IRunStylePropertiesOptions, 'strike'> {
  if (typeof textFormats.strikethrough?.active !== 'boolean') return {};
  return { strike: textFormats.strikethrough.active };
}
